/**
 * File Type Utility Functions
 * 
 * Collection of utility functions for determining and displaying file type information
 * throughout the MacSafety web application. These functions help provide user-friendly
 * file type information and appropriate icons based on MIME types and file extensions.
 * 
 * Key Functions:
 * - getUserFriendlyFileType: Gets comprehensive file type info with display name and icon
 * - getFileTypeDisplayName: Gets just the user-friendly display name
 * - getFileTypeIonicIcon: Gets just the appropriate Ionic icon
 * - isPdf: Checks if a file is a PDF
 * - isImage: Checks if a file is an image
 * 
 * File Type Categories Supported:
 * - Documents: PDF, Word, Excel, PowerPoint, Text, CSV, JSON
 * - Images: JPEG, PNG, GIF, SVG
 * - Archives: ZIP, RAR
 * - Audio/Video: MP3, WAV, MP4, AVI, MOV
 * 
 * Primary Use Cases:
 * - Form view components for displaying file type information
 * - Form view render components for proper file type display
 * - Attachment grid viewer for file type icons and names
 * - File upload components for type validation and display
 * - Any component requiring file type identification and display
 * 
 * Components that utilize these functions:
 * - FormViewComponent: For displaying file type information in form views
 * - FormViewRenderComponent: For rendering file type data in form displays
 * - AttachmentGridViewerComponent: For showing file type icons and names
 * - Any component requiring file type analysis and display
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

/**
 * Interface defining file type information for display purposes
 * Contains user-friendly display name and appropriate Ionic icon
 */
export interface FileTypeInfo {
  displayName: string;  // User-friendly name for the file type
  ionicIcon: string;    // Ionic icon name for visual representation
}

/**
 * MIME type to file type information mapping
 * Maps standard MIME types to display information and icons
 * Used as the primary lookup method for file type identification
 */
const FILE_TYPE_MAP: Record<string, FileTypeInfo> = {
  // Documents
  'application/pdf': { displayName: 'PDF', ionicIcon: 'document-text-outline' },
  'application/msword': { displayName: 'Word Document', ionicIcon: 'document-outline' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { 
    displayName: 'Word Document', 
    ionicIcon: 'document-outline' 
  },
  'application/vnd.ms-excel': { displayName: 'Excel Spreadsheet', ionicIcon: 'grid-outline' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { 
    displayName: 'Excel Spreadsheet', 
    ionicIcon: 'grid-outline' 
  },
  'application/vnd.ms-powerpoint': { displayName: 'PowerPoint', ionicIcon: 'easel-outline' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { 
    displayName: 'PowerPoint', 
    ionicIcon: 'easel-outline' 
  },
  
  // Images
  'image/jpeg': { displayName: 'Image', ionicIcon: 'image-outline' },
  'image/jpg': { displayName: 'Image', ionicIcon: 'image-outline' },
  'image/png': { displayName: 'Image', ionicIcon: 'image-outline' },
  'image/gif': { displayName: 'Image', ionicIcon: 'image-outline' },
  'image/svg+xml': { displayName: 'Image', ionicIcon: 'image-outline' },
  
  // Text
  'text/plain': { displayName: 'Text File', ionicIcon: 'document-text-outline' },
  'text/csv': { displayName: 'CSV File', ionicIcon: 'grid-outline' },
  'application/json': { displayName: 'JSON File', ionicIcon: 'document-text-outline' },
  
  // Archives
  'application/zip': { displayName: 'ZIP Archive', ionicIcon: 'archive-outline' },
  'application/x-rar-compressed': { displayName: 'RAR Archive', ionicIcon: 'archive-outline' },
  
  // Audio/Video
  'audio/mpeg': { displayName: 'Audio File', ionicIcon: 'musical-notes-outline' },
  'audio/mp3': { displayName: 'Audio File', ionicIcon: 'musical-notes-outline' },
  'video/mp4': { displayName: 'Video File', ionicIcon: 'videocam-outline' },
  'video/mpeg': { displayName: 'Video File', ionicIcon: 'videocam-outline' },
  'video/quicktime': { displayName: 'Video File', ionicIcon: 'videocam-outline' },
};

/**
 * File extension to file type information mapping
 * Maps file extensions to display information and icons
 * Used as fallback when MIME type is not available or recognized
 */
const EXTENSION_TYPE_MAP: Record<string, FileTypeInfo> = {
  // Documents
  'txt': { displayName: 'Text File', ionicIcon: 'document-text-outline' },
  'doc': { displayName: 'Word Document', ionicIcon: 'document-outline' },
  'docx': { displayName: 'Word Document', ionicIcon: 'document-outline' },
  'pdf': { displayName: 'PDF', ionicIcon: 'document-text-outline' },
  'xls': { displayName: 'Excel Spreadsheet', ionicIcon: 'grid-outline' },
  'xlsx': { displayName: 'Excel Spreadsheet', ionicIcon: 'grid-outline' },
  'ppt': { displayName: 'PowerPoint', ionicIcon: 'easel-outline' },
  'pptx': { displayName: 'PowerPoint', ionicIcon: 'easel-outline' },
  
  // Images
  'jpg': { displayName: 'Image', ionicIcon: 'image-outline' },
  'jpeg': { displayName: 'Image', ionicIcon: 'image-outline' },
  'png': { displayName: 'Image', ionicIcon: 'image-outline' },
  'gif': { displayName: 'Image', ionicIcon: 'image-outline' },
  'svg': { displayName: 'Image', ionicIcon: 'image-outline' },
  
  // Archives
  'zip': { displayName: 'ZIP Archive', ionicIcon: 'archive-outline' },
  'rar': { displayName: 'RAR Archive', ionicIcon: 'archive-outline' },
  
  // Audio/Video
  'mp3': { displayName: 'Audio File', ionicIcon: 'musical-notes-outline' },
  'wav': { displayName: 'Audio File', ionicIcon: 'musical-notes-outline' },
  'mp4': { displayName: 'Video File', ionicIcon: 'videocam-outline' },
  'avi': { displayName: 'Video File', ionicIcon: 'videocam-outline' },
  'mov': { displayName: 'Video File', ionicIcon: 'videocam-outline' },
};

/**
 * Helper function to extract file type information from file extension
 * Used as fallback when MIME type lookup fails
 * 
 * @param fileName - The filename to extract extension from
 * @returns FileTypeInfo for the extension or null if not found
 */
function getTypeFromExtension(fileName?: string): FileTypeInfo | null {
  if (!fileName) return null;
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension ? EXTENSION_TYPE_MAP[extension] || null : null;
}

/**
 * Gets comprehensive file type information including display name and icon
 * Uses MIME type as primary lookup method, falls back to file extension
 * Provides user-friendly file type information for display purposes
 * 
 * @param contentType - The MIME type of the file
 * @param fileName - The filename for fallback extension lookup
 * @returns FileTypeInfo with display name and appropriate Ionic icon
 */
export function getUserFriendlyFileType(contentType: string, fileName?: string): FileTypeInfo {
  // Try MIME type first for most accurate identification
  const mapped = FILE_TYPE_MAP[contentType?.toLowerCase()];
  if (mapped) return mapped;
  
  // Fallback to extension-based lookup if MIME type not found
  const extensionType = getTypeFromExtension(fileName);
  if (extensionType) return extensionType;
  
  // Final fallback for unknown file types
  return { displayName: 'File', ionicIcon: 'document-outline' };
}

/**
 * Convenience function to get just the user-friendly display name
 * Useful when only the text representation is needed
 * 
 * @param contentType - The MIME type of the file
 * @param fileName - The filename for fallback extension lookup
 * @returns User-friendly display name for the file type
 */
export function getFileTypeDisplayName(contentType: string, fileName?: string): string {
  return getUserFriendlyFileType(contentType, fileName).displayName;
}

/**
 * Convenience function to get just the appropriate Ionic icon
 * Useful when only the icon is needed for display
 * 
 * @param contentType - The MIME type of the file
 * @param fileName - The filename for fallback extension lookup
 * @returns Ionic icon name for the file type
 */
export function getFileTypeIonicIcon(contentType: string, fileName?: string): string {
  return getUserFriendlyFileType(contentType, fileName).ionicIcon;
}

/**
 * Checks if a file is a PDF based on its type information
 * Useful for conditional logic that depends on file type
 * 
 * @param contentType - The MIME type of the file
 * @param fileName - The filename for fallback extension lookup
 * @returns True if the file is a PDF, false otherwise
 */
export function isPdf(contentType: string, fileName?: string): boolean {
  const type = getUserFriendlyFileType(contentType, fileName);
  return type.displayName.toLowerCase() === 'pdf';
}

/**
 * Checks if a file is an image based on its type information
 * Useful for conditional logic that depends on file type
 * 
 * @param contentType - The MIME type of the file
 * @param fileName - The filename for fallback extension lookup
 * @returns True if the file is an image, false otherwise
 */
export function isImage(contentType: string, fileName?: string): boolean {
  const type = getUserFriendlyFileType(contentType, fileName);
  return type.displayName.toLowerCase() === 'image';
}
  